/**
 * Payroll Dashboard — landing page with period status overview and quick links.
 */
import {
  Banknote, CalendarDays, FileSpreadsheet, Gift, Landmark, Settings, Tag, UserSearch, Wallet,
} from "lucide-react";
import { useAuth } from "#/context/AuthProvider";
import { payrollApi } from "../api";
import { useAsyncData } from "../hooks";
import { monthLabel } from "../types";
import {
  Card, LinkBtn, PageHeader, PayrollError, PayrollLoading, PeriodStatusBadge,
} from "../components/PayrollUI";

export default function PayrollDashboardPage() {
  const { hasRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasRole("ADMIN");
  const canProcess = isAdmin || hasPermission("HR_PAYROLL_PROCESS") || hasPermission("HR_PAYROLL_MANAGE");
  const canManage = isAdmin || hasPermission("HR_PAYROLL_MANAGE");
  const canView = isAdmin || hasPermission("HR_PAYROLL_VIEW") || canProcess || hasPermission("HR_PAYROLL_APPROVE");

  const { data, loading, error, reload } = useAsyncData(() => payrollApi.listPeriods(0, 5), []);

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Finance"
        title="Finance"
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <LinkBtn to="/payroll/periods" variant="primary" icon={CalendarDays} className="justify-center py-3">Periods</LinkBtn>
        <LinkBtn to="/payroll/payslips/mine" variant="secondary" icon={Wallet} className="justify-center py-3">My Payslips</LinkBtn>
        <LinkBtn to="/payroll/bank-account/mine" variant="secondary" icon={Landmark} className="justify-center py-3">My Bank Account</LinkBtn>
        {canView && <LinkBtn to="/payroll/reports" variant="secondary" icon={FileSpreadsheet} className="justify-center py-3">Reports</LinkBtn>}
      </div>

      {canManage && (
        <div className="grid gap-3 sm:grid-cols-3">
          <LinkBtn to="/payroll/config/statutory" variant="ghost" icon={Settings} className="justify-center py-3 border border-slate-200">Statutory Config</LinkBtn>
          <LinkBtn to="/payroll/deduction-categories" variant="ghost" icon={Tag} className="justify-center py-3 border border-slate-200">Deduction Categories</LinkBtn>
          <LinkBtn to="/payroll/employee-deductions" variant="ghost" icon={UserSearch} className="justify-center py-3 border border-slate-200">Employee Deductions</LinkBtn>
          <LinkBtn to="/payroll/benefit-categories" variant="ghost" icon={Gift} className="justify-center py-3 border border-slate-200">Benefit Categories</LinkBtn>
          <LinkBtn to="/payroll/employee-benefits" variant="ghost" icon={Gift} className="justify-center py-3 border border-slate-200">Employee Benefits</LinkBtn>
          <LinkBtn to="/payroll/bank-accounts" variant="ghost" icon={Banknote} className="justify-center py-3 border border-slate-200">Employee Bank Accounts</LinkBtn>
        </div>
      )}

      {canView && (
        <Card noPad>
          <div className="flex items-center justify-between border-b px-5 py-4">
            <h2 className="font-semibold text-slate-900">Recent Periods</h2>
            <LinkBtn to="/payroll/periods" variant="ghost" size="sm">View all</LinkBtn>
          </div>
          {loading && <div className="p-5"><PayrollLoading /></div>}
          {error && <div className="p-5"><PayrollError message={error} onRetry={reload} /></div>}
          {!loading && !error && (
            <div className="divide-y divide-slate-100">
              {!(data?.content.length) ? (
                <p className="p-5 text-sm text-slate-400">No finance periods yet.</p>
              ) : data!.content.map((p) => (
                <LinkBtn key={p.id} to={`/payroll/periods/${p.id}`} variant="ghost" className="flex w-full items-center justify-between rounded-none px-5 py-3">
                  <span className="font-medium text-slate-800">{monthLabel(p.periodMonth)} {p.periodYear}</span>
                  <PeriodStatusBadge value={p.status} />
                </LinkBtn>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
